import {
  useBasketEvents,
  useShopEvents,
  useWishlistEvents,
  useProductEvents,
  useCheckoutEvents,
  useSearchEvents,
  usePurchaseEvents,
  usePromotionEvents,
  useUserActionEvents,
  useCustomEvents,
  useTrboEvents,
  useYouTubeEvents,
  useNavigationEvents,
  useBlogEvents,
  useOdoscopeEvents,
  useExperimentsEvents,
} from './events'
import { mapProductToTrackingPayload, enrichTrackingData } from '~/utils/tracking'
import type { TrackingEvent, TrackingPayload } from '~/types/tracking'
import { useState } from '#app/composables/state'

/**
 * Higher order composable which serves as a single entry point to unify different ecommerce tracking events
 */
const useTrackingEvents = (useServerQueue?: boolean) => {
  const { $tracking } = useNuxtApp()
  const route = useRoute()

  const serverQueue = useState<any[]>('serverQueue', () => [])

  const track = (event: TrackingEvent, payload: TrackingPayload) => {
    const trackingData = enrichTrackingData(event, payload, route)

    if (import.meta.server) {
      if (useServerQueue) {
        serverQueue.value = [...serverQueue.value, trackingData]
      }
    } else {
      $tracking.push(trackingData)
    }
  }

  const fireTrackingServerQueue = () => {
    if (useServerQueue) {
      serverQueue.value.map((trackingData) => $tracking.push(trackingData))
      serverQueue.value = []
    }
  }

  /**
   * NOTE: Important considerations:
   * sub-composable(s) such as useBasketEvents, etc should not be utilized directly
   * instead make use of the higher order composable (useTrackingEvents) to keep things clean
   * new events should be added to relevant sub-composable to ensure DRY code and separation of concerns
   */

  return {
    ...useShopEvents(track),
    ...useBasketEvents(track),
    ...useWishlistEvents(track),
    ...useProductEvents(track),
    ...useCheckoutEvents(track),
    ...useSearchEvents(track),
    ...usePurchaseEvents(track, { $tracking }),
    ...usePromotionEvents(track),
    ...useUserActionEvents(track, { $tracking }),
    ...useCustomEvents(track),
    ...useTrboEvents(track),
    ...useYouTubeEvents(track),
    ...useNavigationEvents(track),
    ...useBlogEvents(track),
    ...useOdoscopeEvents(track),
    ...useExperimentsEvents(track),
    mapProductToTrackingPayload,
    fireTrackingServerQueue,
  }
}

export default useTrackingEvents
